// <pos-scheduling-settings/> — 排班基礎資料：員工 / 固定排休 / 期望假 / 班別模板
// 路徑：#/settings/scheduling（owner only — employees 含薪資欄位，RLS owner-only）
// 直連 supabase（RLS 把關），寫入需帶 tenant_id（讀 body dataset，main.js 注入）
// see architecture.md#L980 + docs/SCHEDULING_SYSTEM_SPEC.md v0.2

import { getSupabase } from '../lib/supabase-client.js';
import { escapeHtml } from '../lib/utils/escape.js';

const ROLE_LABELS = { chef: '主廚', fulltime: '正職', parttime: '兼職' };
const WD_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

class PosSchedulingSettings extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      storeId: null,
      employees: [],
      recurringOff: [],   // [{employee_id, weekday, priority}] must=必須休 prefer=偏好休
      timeOff: [],        // 未來的期望假
      partnerships: [],   // [{employee_a, employee_b, kind}] prefer=合拍 avoid=避開
      profiles: [],       // 同 tenant 的登入帳號（員工綁定下拉用）
      templates: [],
      loading: true,
      error: null,
    };
  }

  get tenantId() {
    return document.body.dataset.tenantId || null;
  }

  async connectedCallback() {
    this.render();
    await this.load();
  }

  async load() {
    const supabase = getSupabase();
    try {
      const { data: store } = await supabase.from('stores')
        .select('id').eq('is_active', true)
        .order('created_at', { ascending: true }).limit(1).maybeSingle();
      this.state.storeId = store?.id || null;

      const today = new Date().toISOString().slice(0, 10);
      const [emp, off, req, tpl, ptn, prf] = await Promise.all([
        supabase.from('employees')
          .select('id, name, role, hourly_rate, weekly_hour_min, weekly_hour_max, is_active, profile_id')
          .order('created_at', { ascending: true }),
        supabase.from('employee_recurring_off').select('employee_id, weekday, priority'),
        supabase.from('employee_time_off_requests')
          .select('id, employee_id, date, priority, status, note')
          .gte('date', today).order('date', { ascending: true }),
        supabase.from('shift_templates')
          .select('id, name, start_time, end_time, weekdays, min_chef, min_total, is_active')
          .eq('is_active', true).order('start_time', { ascending: true }),
        supabase.from('employee_partnerships').select('employee_a, employee_b, kind'),
        supabase.from('profiles').select('id, display_name, role').order('display_name'),
      ]);
      const firstErr = emp.error || off.error || req.error || tpl.error || ptn.error || prf.error;
      if (firstErr) throw firstErr;
      this.state.employees = emp.data || [];
      this.state.recurringOff = off.data || [];
      this.state.timeOff = req.data || [];
      this.state.templates = tpl.data || [];
      this.state.partnerships = ptn.data || [];
      this.state.profiles = prf.data || [];
      this.state.error = null;
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  // ─── 員工 ───
  async addEmployee() {
    const root = this.shadowRoot;
    const name = root.querySelector('#empName')?.value.trim();
    const role = root.querySelector('#empRole')?.value || 'parttime';
    const rateRaw = root.querySelector('#empRate')?.value.trim();
    const hourly_rate = rateRaw ? Number(rateRaw) : null;
    if (!name) return alert('請輸入員工姓名');
    if (hourly_rate != null && (!Number.isFinite(hourly_rate) || hourly_rate < 0)) {
      return alert('時薪格式錯誤');
    }
    const { error } = await getSupabase().from('employees').insert({
      tenant_id: this.tenantId, store_id: this.state.storeId, name, role, hourly_rate,
    });
    if (error) return alert('新增失敗：' + error.message);
    await this.load();
  }

  async updateRate(id, raw) {
    const hourly_rate = raw === '' ? null : Number(raw);
    if (hourly_rate != null && (!Number.isFinite(hourly_rate) || hourly_rate < 0)) {
      return alert('時薪格式錯誤');
    }
    const { error } = await getSupabase().from('employees')
      .update({ hourly_rate }).eq('id', id);
    if (error) return alert('儲存失敗：' + error.message);
    const emp = this.state.employees.find((e) => e.id === id);
    if (emp) emp.hourly_rate = hourly_rate;  // 不整頁 reload，避免 input 失焦
  }

  async removeEmployee(id) {
    if (!confirm('刪除這位員工？（已排班表中的紀錄會保留）')) return;
    // 有班表引用時 FK 會擋 hard delete → 改 soft delete
    const supabase = getSupabase();
    const { error } = await supabase.from('employees').delete().eq('id', id);
    if (error) {
      const { error: e2 } = await supabase.from('employees')
        .update({ is_active: false }).eq('id', id);
      if (e2) return alert('刪除失敗：' + e2.message);
    }
    await this.load();
  }

  /** 固休 chip 三態循環：無 → 必須休(must) → 偏好休(prefer) → 無 */
  async toggleRecurringOff(employeeId, weekday) {
    const supabase = getSupabase();
    const cur = this.state.recurringOff
      .find((r) => r.employee_id === employeeId && r.weekday === weekday);
    let error;
    if (!cur) {
      ({ error } = await supabase.from('employee_recurring_off')
        .insert({ tenant_id: this.tenantId, employee_id: employeeId, weekday, priority: 'must' }));
    } else if (cur.priority === 'must') {
      ({ error } = await supabase.from('employee_recurring_off')
        .update({ priority: 'prefer' })
        .eq('employee_id', employeeId).eq('weekday', weekday));
    } else {
      ({ error } = await supabase.from('employee_recurring_off')
        .delete().eq('employee_id', employeeId).eq('weekday', weekday));
    }
    if (error) return alert('儲存失敗：' + error.message);
    await this.load();
  }

  // ─── 搭配偏好 ───
  async addPartnership() {
    const root = this.shadowRoot;
    const a = root.querySelector('#ptnA')?.value;
    const b = root.querySelector('#ptnB')?.value;
    const kind = root.querySelector('#ptnKind')?.value || 'prefer';
    if (!a || !b) return alert('請選兩位員工');
    if (a === b) return alert('請選擇兩位不同的員工');
    const [employee_a, employee_b] = a < b ? [a, b] : [b, a];  // DB CHECK a < b
    const { error } = await getSupabase().from('employee_partnerships')
      .insert({ tenant_id: this.tenantId, employee_a, employee_b, kind });
    if (error) return alert('新增失敗：' + (error.code === '23505' ? '這組搭配已存在' : error.message));
    await this.load();
  }

  async removePartnership(a, b) {
    const { error } = await getSupabase().from('employee_partnerships')
      .delete().eq('employee_a', a).eq('employee_b', b);
    if (error) return alert('刪除失敗：' + error.message);
    await this.load();
  }

  // ─── 員工綁定登入帳號（W13：員工端 my-shifts 靠 employees.profile_id 認人）───
  async bindProfile(employeeId, profileId) {
    const { error } = await getSupabase().from('employees')
      .update({ profile_id: profileId || null }).eq('id', employeeId);
    if (error) return alert('綁定失敗：' + error.message);
    const emp = this.state.employees.find((e) => e.id === employeeId);
    if (emp) emp.profile_id = profileId || null;   // 不整頁 reload，避免下拉失焦
  }

  // ─── 請假准駁（員工端送出 status=requested，這裡核准/拒絕）───
  async reviewTimeOff(id, status) {
    const { error } = await getSupabase().from('employee_time_off_requests')
      .update({ status }).eq('id', id);
    if (error) return alert('操作失敗：' + error.message);
    await this.load();
  }

  // ─── 期望假 ───
  async addTimeOff() {
    const root = this.shadowRoot;
    const employee_id = root.querySelector('#toEmp')?.value;
    const date = root.querySelector('#toDate')?.value;
    const priority = root.querySelector('#toPriority')?.value || 'prefer';
    if (!employee_id || !date) return alert('請選員工與日期');
    const { error } = await getSupabase().from('employee_time_off_requests')
      .insert({ tenant_id: this.tenantId, employee_id, date, priority, status: 'approved' });
    if (error) return alert('新增失敗：' + (error.code === '23505' ? '該員工此日已有登記' : error.message));
    await this.load();
  }

  async removeTimeOff(id) {
    const { error } = await getSupabase().from('employee_time_off_requests').delete().eq('id', id);
    if (error) return alert('刪除失敗：' + error.message);
    await this.load();
  }

  // ─── 班別模板 ───
  async addTemplate() {
    const root = this.shadowRoot;
    const name = root.querySelector('#tplName')?.value.trim();
    const start_time = root.querySelector('#tplStart')?.value;
    const end_time = root.querySelector('#tplEnd')?.value;
    const min_total = Number(root.querySelector('#tplMin')?.value || 2);
    const min_chef = Number(root.querySelector('#tplChef')?.value || 0);
    const weekdays = [...root.querySelectorAll('[data-tpl-wd]:checked')]
      .map((c) => Number(c.dataset.tplWd));
    if (!name || !start_time || !end_time) return alert('請填齊班別名稱與時間');
    if (start_time >= end_time) return alert('結束時間需晚於開始時間（跨夜班別 Phase 2 支援）');
    if (!weekdays.length) return alert('請至少勾選一個適用星期');
    const { error } = await getSupabase().from('shift_templates').insert({
      tenant_id: this.tenantId, store_id: this.state.storeId,
      name, start_time, end_time, weekdays, min_total, min_chef,
    });
    if (error) return alert('新增失敗：' + (error.code === '23505' ? '班別名稱重複' : error.message));
    await this.load();
  }

  async removeTemplate(id) {
    if (!confirm('停用這個班別？')) return;
    const { error } = await getSupabase().from('shift_templates')
      .update({ is_active: false }).eq('id', id);
    if (error) return alert('停用失敗：' + error.message);
    await this.load();
  }

  render() {
    const { employees, recurringOff, timeOff, partnerships, profiles, templates, loading, error } = this.state;
    const pendingReqs = timeOff.filter((t) => t.status === 'requested');
    const decidedOff = timeOff.filter((t) => t.status !== 'requested');
    const empName = (id) => escapeHtml(employees.find((e) => e.id === id)?.name || '—');
    const offState = (empId, wd) => recurringOff
      .find((r) => r.employee_id === empId && r.weekday === wd)?.priority || '';

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <main>
        <aside class="sub-nav">
          <div class="sub-nav__title">設定</div>
          <a class="sub-nav__item" href="#/settings">店家資料</a>
          <a class="sub-nav__item" href="#/settings/reservation">訂位設定</a>
          <a class="sub-nav__item is-active" href="#/settings/scheduling">排班</a>
          <a class="sub-nav__item" href="#/settings/stations">站點與通知</a>
        </aside>

        <section class="panel">
          <div class="panel__head">
            <h1 class="panel__title">排班設定</h1>
            <p class="panel__desc">先建員工與班別，再到「排班」頁一鍵生成班表。固休與期望假是排班的硬約束。</p>
          </div>
          ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
          ${loading ? '<div class="banner loading">載入中…</div>' : `

          <div class="card">
            <h2 class="card__title">員工（${employees.filter((e) => e.is_active).length}）</h2>
            <p class="card__desc">排休格子點一下 = <span class="lgd lgd--must">必須休</span>（絕不排班），再點 = <span class="lgd lgd--prefer">偏好休</span>（盡量避開），再點 = 取消。</p>
            <table>
              <thead><tr><th>姓名</th><th>角色</th><th>時薪 NT$</th><th>登入帳號</th><th>每週排休偏好</th><th></th></tr></thead>
              <tbody>
                ${employees.filter((e) => e.is_active).map((e) => `
                  <tr>
                    <td><strong>${escapeHtml(e.name)}</strong></td>
                    <td>${ROLE_LABELS[e.role] || escapeHtml(e.role)}</td>
                    <td><input type="number" class="num rate" min="0" step="1" data-rate="${escapeHtml(e.id)}"
                          value="${e.hourly_rate ?? ''}" placeholder="—" title="時薪（人力統計用來估算薪資；月薪制可留空）" /></td>
                    <td>
                      <select data-bind-profile="${escapeHtml(e.id)}" title="綁定後員工登入可在「我的班表」看班、線上請假">
                        <option value="">未綁定</option>
                        ${profiles.map((p) => `<option value="${escapeHtml(p.id)}" ${p.id === e.profile_id ? 'selected' : ''}>${escapeHtml(p.display_name || p.id.slice(0, 8))}</option>`).join('')}
                      </select>
                    </td>
                    <td class="wds">
                      ${WD_LABELS.map((l, wd) => `
                        <button class="wd-chip ${wd === 0 || wd === 6 ? 'wd-chip--weekend' : ''}" data-act="toggle-off" data-emp="${escapeHtml(e.id)}" data-wd="${wd}"
                          data-state="${offState(e.id, wd)}"
                          title="${offState(e.id, wd) === 'must' ? '必須休' : offState(e.id, wd) === 'prefer' ? '偏好休' : '可排班'}">${l}</button>
                      `).join('')}
                    </td>
                    <td><button class="del" data-act="del-emp" data-id="${escapeHtml(e.id)}">刪除</button></td>
                  </tr>
                `).join('') || '<tr><td colspan="6" class="empty">還沒有員工</td></tr>'}
              </tbody>
            </table>
            <div class="form-row">
              <input id="empName" type="text" maxlength="50" placeholder="姓名" />
              <select id="empRole">
                ${Object.entries(ROLE_LABELS).map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}
              </select>
              <input id="empRate" type="number" class="num rate" min="0" step="1" placeholder="時薪（選填）" />
              <button class="primary" data-act="add-emp">＋ 新增員工</button>
            </div>
          </div>

          <div class="card">
            <h2 class="card__title">搭配偏好</h2>
            <p class="card__desc">「常搭檔」= 排班時盡量排同一班（例：主廚阿明搭內場小美）；「不同班」= 絕不排在同一班。</p>
            ${partnerships.length ? `<table><tbody>
              ${partnerships.map((p) => `
                <tr>
                  <td>${empName(p.employee_a)} ${p.kind === 'prefer' ? '🤝' : '🚫'} ${empName(p.employee_b)}</td>
                  <td>${p.kind === 'prefer' ? '常搭檔（盡量同班）' : '不同班（絕不同班）'}</td>
                  <td><button class="del" data-act="del-ptn" data-a="${escapeHtml(p.employee_a)}" data-b="${escapeHtml(p.employee_b)}">移除</button></td>
                </tr>`).join('')}
            </tbody></table>` : ''}
            <div class="form-row">
              <select id="ptnA">
                ${employees.filter((e) => e.is_active).map((e) =>
                  `<option value="${escapeHtml(e.id)}">${escapeHtml(e.name)}</option>`).join('')}
              </select>
              <select id="ptnKind">
                <option value="prefer">🤝 合拍</option>
                <option value="avoid">🚫 避開</option>
              </select>
              <select id="ptnB">
                ${employees.filter((e) => e.is_active).map((e, i) =>
                  `<option value="${escapeHtml(e.id)}" ${i === 1 ? 'selected' : ''}>${escapeHtml(e.name)}</option>`).join('')}
              </select>
              <button class="primary" data-act="add-ptn">＋ 新增搭配</button>
            </div>
          </div>

          ${pendingReqs.length ? `
          <div class="card">
            <h2 class="card__title">🔔 員工請假申請（${pendingReqs.length} 筆待審）</h2>
            <p class="card__desc">員工在「我的班表」送出的申請。核准後成為排班硬/軟約束；拒絕會通知不過。</p>
            <table><tbody>
              ${pendingReqs.map((t) => `
                <tr>
                  <td>${empName(t.employee_id)}</td>
                  <td>${escapeHtml(t.date)}</td>
                  <td>${t.priority === 'must' ? '🔴 必須' : '🟡 希望'}</td>
                  <td class="note-cell">${escapeHtml(t.note || '')}</td>
                  <td>
                    <button class="primary" data-act="approve-to" data-id="${escapeHtml(t.id)}">核准</button>
                    <button class="del" data-act="reject-to" data-id="${escapeHtml(t.id)}">拒絕</button>
                  </td>
                </tr>`).join('')}
            </tbody></table>
          </div>` : ''}

          <div class="card">
            <h2 class="card__title">期望假（特定日期）</h2>
            <p class="card__desc">「必須」= 絕不排班；「希望」= 盡量避開，人手不足時仍可能排到（班表會標註）。</p>
            ${decidedOff.length ? `<table><tbody>
              ${decidedOff.map((t) => `
                <tr>
                  <td>${empName(t.employee_id)}</td>
                  <td>${escapeHtml(t.date)}</td>
                  <td>${t.priority === 'must' ? '🔴 必須' : '🟡 希望'}</td>
                  <td>${t.status === 'rejected' ? '❌ 已拒' : '✅'}</td>
                  <td><button class="del" data-act="del-to" data-id="${escapeHtml(t.id)}">取消</button></td>
                </tr>`).join('')}
            </tbody></table>` : ''}
            <div class="form-row">
              <select id="toEmp">
                ${employees.filter((e) => e.is_active).map((e) =>
                  `<option value="${escapeHtml(e.id)}">${escapeHtml(e.name)}</option>`).join('')}
              </select>
              <input id="toDate" type="date" />
              <select id="toPriority">
                <option value="prefer">希望排休</option>
                <option value="must">必須排休</option>
              </select>
              <button class="primary" data-act="add-to">＋ 登記</button>
            </div>
          </div>

          <div class="card">
            <h2 class="card__title">班別模板</h2>
            ${templates.length ? `<table>
              <thead><tr><th>班別</th><th>時間</th><th>適用</th><th>最低人力</th><th></th></tr></thead>
              <tbody>
                ${templates.map((t) => `
                  <tr>
                    <td><strong>${escapeHtml(t.name)}</strong></td>
                    <td>${escapeHtml(String(t.start_time).slice(0, 5))}–${escapeHtml(String(t.end_time).slice(0, 5))}</td>
                    <td>${(t.weekdays || []).map((w) => WD_LABELS[w]).join('、')}</td>
                    <td>${t.min_total} 人${t.min_chef ? `（含主廚 ${t.min_chef}）` : ''}</td>
                    <td><button class="del" data-act="del-tpl" data-id="${escapeHtml(t.id)}">停用</button></td>
                  </tr>`).join('')}
              </tbody></table>` : '<div class="empty">還沒有班別。例：早班 08:00–14:00、晚班 14:00–21:00</div>'}
            <div class="form-row wrap">
              <input id="tplName" type="text" maxlength="30" placeholder="班別名稱（例：早班）" />
              <input id="tplStart" type="time" value="08:00" />
              <span>到</span>
              <input id="tplEnd" type="time" value="14:00" />
              <label>最低 <input id="tplMin" type="number" min="1" max="50" value="2" class="num" /> 人</label>
              <label>含主廚 <input id="tplChef" type="number" min="0" max="10" value="0" class="num" /> 人</label>
              <span class="wds">
                ${WD_LABELS.map((l, wd) => `
                  <label class="wd-check"><input type="checkbox" data-tpl-wd="${wd}" ${wd >= 1 && wd <= 5 ? 'checked' : ''}/>${l}</label>
                `).join('')}
              </span>
              <button class="primary" data-act="add-tpl">＋ 新增班別</button>
            </div>
          </div>
          `}
        </section>
      </main>
    `;
    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    const on = (sel, fn) => root.querySelectorAll(sel).forEach((el) =>
      el.addEventListener('click', () => fn(el)));
    on('[data-act="add-emp"]', () => this.addEmployee());
    on('[data-act="del-emp"]', (el) => this.removeEmployee(el.dataset.id));
    on('[data-act="toggle-off"]', (el) => this.toggleRecurringOff(el.dataset.emp, Number(el.dataset.wd)));
    on('[data-act="add-to"]', () => this.addTimeOff());
    on('[data-act="del-to"]', (el) => this.removeTimeOff(el.dataset.id));
    on('[data-act="add-ptn"]', () => this.addPartnership());
    on('[data-act="del-ptn"]', (el) => this.removePartnership(el.dataset.a, el.dataset.b));
    root.querySelectorAll('[data-rate]').forEach((inp) =>
      inp.addEventListener('change', () => this.updateRate(inp.dataset.rate, inp.value.trim())));
    root.querySelectorAll('[data-bind-profile]').forEach((sel) =>
      sel.addEventListener('change', () => this.bindProfile(sel.dataset.bindProfile, sel.value)));
    on('[data-act="approve-to"]', (el) => this.reviewTimeOff(el.dataset.id, 'approved'));
    on('[data-act="reject-to"]', (el) => this.reviewTimeOff(el.dataset.id, 'rejected'));
    on('[data-act="add-tpl"]', () => this.addTemplate());
    on('[data-act="del-tpl"]', (el) => this.removeTemplate(el.dataset.id));
  }

  styles() {
    return `
      :host { display: block; color: var(--fg-base); background: var(--bg-base); min-height: calc(100vh - 60px); font-family: var(--font-sans); }
      main { display: grid; grid-template-columns: 220px 1fr; gap: var(--s-6); padding: var(--s-6) var(--s-8); }
      /* 手機：設定導覽收合到內容上方，單欄堆疊 */
      @media (max-width: 768px) {
        main { grid-template-columns: 1fr; gap: var(--s-4); padding: var(--s-4); }
      }
      .sub-nav { display: grid; gap: var(--s-1); align-content: start; }
      .sub-nav__title { font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; color: var(--fg-dim); padding: 0 var(--s-3) var(--s-2); }
      .sub-nav__item { padding: var(--s-3) var(--s-4); color: var(--fg-muted); text-decoration: none; border-radius: var(--r-md); font-size: .9rem; }
      .sub-nav__item:hover { background: var(--bg-elevated); color: var(--fg-base); }
      .sub-nav__item.is-active { background: var(--bg-elevated); color: var(--brand-primary); border-left: 3px solid var(--brand-primary); padding-left: calc(var(--s-4) - 3px); }
      .panel { display: grid; gap: var(--s-5); align-content: start; }
      .panel__head { display: grid; gap: var(--s-2); }
      .panel__title { font-size: 1.4rem; font-weight: 600; margin: 0; }
      .panel__desc { color: var(--fg-muted); font-size: .9rem; margin: 0; }
      .card { padding: var(--s-5); background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); display: grid; gap: var(--s-4); }
      .card__title { font-size: 1.05rem; font-weight: 600; margin: 0; }
      .card__desc { color: var(--fg-muted); font-size: .85rem; margin: 0; }
      .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); }
      .banner.err { background: rgba(239,68,68,.1); color: var(--danger); }
      .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }
      table { width: 100%; border-collapse: collapse; font-size: .9rem; }
      th, td { padding: var(--s-2) var(--s-3); text-align: left; border-bottom: 1px solid var(--border); }
      th { font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; color: var(--fg-dim); font-weight: 500; }
      .empty { color: var(--fg-dim); text-align: center; padding: var(--s-4); }
      .form-row { display: flex; gap: var(--s-2); align-items: center; flex-wrap: nowrap; }
      .form-row.wrap { flex-wrap: wrap; }
      input, select { background: var(--bg-base); color: var(--fg-base); border: 1px solid var(--border); border-radius: var(--r-md); padding: var(--s-2) var(--s-3); font-family: inherit; font-size: .9rem; min-height: 40px; }
      input:focus, select:focus { outline: none; border-color: var(--brand-primary); }
      input.num { width: 56px; }
      input.num.rate { width: 110px; }
      button { font-family: inherit; cursor: pointer; border-radius: var(--r-md); border: 1px solid var(--border); background: var(--bg-surface); color: var(--fg-base); min-height: 40px; padding: var(--s-2) var(--s-3); }
      button.primary { background: var(--brand-primary); color: var(--fg-on-brand); border: none; font-weight: 600; }
      button.del { color: var(--danger); border-color: transparent; background: transparent; min-height: 32px; }
      .wds { display: inline-flex; gap: 4px; flex-wrap: wrap; }
      .wd-chip--weekend { color: var(--danger, #ef4444); }
      .wd-chip { min-width: 32px; min-height: 32px; padding: 2px 6px; font-size: .8rem; border-radius: var(--r-full); }
      .wd-chip[data-state="must"] { background: var(--brand-primary); color: var(--fg-on-brand); border-color: var(--brand-primary); }
      .wd-chip[data-state="prefer"] { background: transparent; color: var(--accent, #C47D00); border: 2px dashed var(--accent, #C47D00); font-weight: 600; }
      .lgd { padding: 1px 8px; border-radius: var(--r-full); font-size: .8rem; }
      .lgd--must { background: var(--brand-primary); color: var(--fg-on-brand); }
      .lgd--prefer { border: 2px dashed var(--accent, #C47D00); color: var(--accent, #C47D00); font-weight: 600; }
      .wd-check { display: inline-flex; align-items: center; gap: 2px; font-size: .85rem; }
      .wd-check input { min-height: auto; width: 16px; height: 16px; accent-color: var(--brand-primary); }
    `;
  }
}

customElements.define('pos-scheduling-settings', PosSchedulingSettings);
